import { useEffect, useState, type FormEvent } from "react";
import { ApiError, register, resetPassword, type CompletionResponse } from "./api";
import { Shell } from "./Shell";
import { SubmitButton } from "./ui";
import { ApiErrorDisplay, ErrorDisplay } from "./ErrorDisplay";

/**
 * Shared "set a new password" UI used by both the reset-password and
 * accept-invite flows. The two differ only in which Worker endpoint
 * they call; everything else (read email+token from URL, password +
 * confirm inputs, error handling, auto-sign-in) is identical.
 */
export type SetPasswordMode = "reset" | "invite";

const MIN_LENGTH = 8;

export function SetPassword({
    mode,
    redirectTo = "/",
}: {
    mode: SetPasswordMode;
    redirectTo?: string;
}) {
    const [email, setEmail] = useState<string | null>(null);
    const [token, setToken] = useState<string | null>(null);
    const [region, setRegion] = useState<string | undefined>(undefined);
    const [paramsBad, setParamsBad] = useState(false);

    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    // Two error channels: `error` is a client-side validation message
    // (passwords-don't-match, too-short, or the Worker's policy error
    // text); `apiError` is a typed transport/server failure surfaced by
    // the polished category-specific banner. Displayed as an
    // either/or.
    const [error, setError] = useState<string | null>(null);
    const [apiError, setApiError] = useState<ApiError | null>(null);
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(false);

    // Read email + token from query string on mount. Both are
    // required; a missing one means the user landed here by typing
    // the path manually or the emailed link was corrupted.
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const e = params.get("email");
        const t = params.get("token");
        const r = params.get("region") ?? undefined;
        if (!e || !t) {
            setParamsBad(true);
            return;
        }
        setEmail(e);
        setToken(t);
        setRegion(r);
    }, []);

    const handle = async (e: FormEvent) => {
        e.preventDefault();
        setError(null);
        setApiError(null);

        if (password !== confirm) {
            setError("The two passwords don't match.");
            return;
        }
        if (password.length < MIN_LENGTH) {
            setError(`Password must be at least ${MIN_LENGTH} characters.`);
            return;
        }
        if (!email || !token) return;

        setBusy(true);
        try {
            const result: CompletionResponse =
                mode === "reset"
                    ? await resetPassword(email, token, password, region)
                    : await register(email, token, password, region);

            if (!result.ok) {
                setError(errorMessage(result.failureCode, result.errors, mode));
                return;
            }
            setDone(true);
            setTimeout(() => {
                window.location.href = redirectTo;
            }, 200);
        } catch (err) {
            if (err instanceof ApiError) {
                setApiError(err);
            } else {
                setError("Something went wrong. Please try again.");
            }
        } finally {
            setBusy(false);
        }
    };

    const heading =
        mode === "reset" ? "Reset your password" : "Complete your invitation";
    const subtitle =
        mode === "reset"
            ? "Choose a new password for your account."
            : "Set a password to finish creating your account.";
    const submitLabel = mode === "reset" ? "Set new password" : "Create account";

    if (paramsBad) {
        return (
            <Shell title="Link invalid" subtitle="This link is invalid or has expired.">
                <p className="inline-error" role="alert">
                    This link is invalid or has expired. Please request a new one.
                </p>
                <p className="hint">
                    <a href="/login" className="link">
                        Back to sign in
                    </a>
                </p>
            </Shell>
        );
    }

    if (done) {
        return (
            <Shell title={mode === "reset" ? "Password updated" : "Account ready"}>
                <div className="done" role="status">
                    <span className="done-tick" aria-hidden="true">✓</span>
                    {mode === "reset" ? "Password updated." : "Account ready."}{" "}
                    Redirecting…
                </div>
            </Shell>
        );
    }

    return (
        <Shell title={heading} subtitle={subtitle}>
            <p className="hello">
                Signing in as <strong>{email}</strong>
            </p>
            <form onSubmit={handle} noValidate>
                <div className="field">
                    <label htmlFor="password" className="label">
                        New password
                    </label>
                    <input
                        id="password"
                        name="new-password"
                        type="password"
                        autoComplete="new-password"
                        autoFocus
                        required
                        minLength={MIN_LENGTH}
                        disabled={busy}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="input"
                    />
                </div>
                <div className="field">
                    <label htmlFor="confirm" className="label">
                        Confirm password
                    </label>
                    <input
                        id="confirm"
                        name="confirm-password"
                        type="password"
                        autoComplete="new-password"
                        required
                        minLength={MIN_LENGTH}
                        disabled={busy}
                        value={confirm}
                        onChange={(e) => setConfirm(e.target.value)}
                        className="input"
                    />
                </div>
                {apiError && <ApiErrorDisplay error={apiError} />}
                {error && <ErrorDisplay message={error} />}
                <div style={{ marginTop: "1.5rem" }}>
                    <SubmitButton
                        busy={busy}
                        disabled={!password || !confirm}
                        label={submitLabel}
                        busyLabel="Working…"
                    />
                </div>
            </form>
        </Shell>
    );
}

function errorMessage(
    code: string,
    errors: string[] | undefined,
    mode: SetPasswordMode,
): string {
    if (code === "already_accepted" && mode === "invite") {
        return "This invitation has already been accepted. Please sign in instead.";
    }
    if (code === "weak_password" && errors && errors.length > 0) {
        // Pass through Identity's policy errors verbatim — they're
        // user-facing by design (length / complexity rules).
        return errors.join(" ");
    }
    if (code === "invalid") {
        return mode === "reset"
            ? "This reset link is invalid or has expired. Please request a new one."
            : "This invitation link is invalid or has expired. Ask your admin to send a new one.";
    }
    return "Something went wrong. Please try again.";
}
